import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  CalendarDays, 
  MapPin, 
  Users, 
  Video,
  GraduationCap,
  Presentation,
  Clock,
  ArrowRight,
  CheckCircle,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = [
  { id: 'webinar', name: 'Webinars', icon: Video },
  { id: 'ce_course', name: 'CE Courses', icon: GraduationCap },
  { id: 'conference', name: 'Conferences', icon: Presentation },
  { id: 'networking', name: 'Networking', icon: Users },
];

const sampleEvents = [
  {
    event_id: 'evt_1',
    title: 'Market Trends 2024: What Every Agent Needs to Know',
    description: 'Join our expert panel as they discuss the latest market trends and predictions for the real estate industry.',
    event_type: 'webinar',
    date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    is_virtual: true,
    max_attendees: 500,
  },
  {
    event_id: 'evt_2',
    title: 'Fair Housing Continuing Education',
    description: 'Complete your CE requirements with this comprehensive fair housing course approved in all 50 states.',
    event_type: 'ce_course',
    date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    is_virtual: true,
    max_attendees: 100,
  },
  {
    event_id: 'evt_3',
    title: 'AREA National Conference 2024',
    description: 'Our flagship annual event bringing together real estate professionals from across the nation.',
    event_type: 'conference',
    date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Las Vegas, NV',
    is_virtual: false,
    max_attendees: 2000,
  },
  {
    event_id: 'evt_4',
    title: 'Northeast Regional Networking Mixer',
    description: 'Connect with fellow AREA members from the Northeast region at this exclusive networking event.',
    event_type: 'networking',
    date: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'New York, NY',
    is_virtual: false,
    max_attendees: 150,
  },
  {
    event_id: 'evt_5',
    title: 'AI in Real Estate: Practical Applications',
    description: 'Learn how artificial intelligence is transforming the real estate industry and how to leverage it.',
    event_type: 'webinar',
    date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
    is_virtual: true,
    max_attendees: 500,
  },
];

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState(sampleEvents);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [registrations, setRegistrations] = useState({});
  const [regLoading, setRegLoading] = useState({});

  const getSessionToken = () => {
    return document.cookie.split('; ').find(row => row.startsWith('session_token='))?.split('=')[1];
  };

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const url = selectedType === 'all' 
          ? `${API}/events` 
          : `${API}/events?event_type=${selectedType}`;
        
        const response = await fetch(url, { credentials: 'include' });
        
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setEvents(data);
          }
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [selectedType]);

  // Fetch registration status for each event if logged in
  useEffect(() => {
    if (!user) return;
    const token = getSessionToken();
    if (!token) return;
    
    events.forEach(async (event) => {
      try {
        const res = await fetch(`${API}/events/${event.event_id}/registration-status`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setRegistrations(prev => ({ ...prev, [event.event_id]: data }));
        }
      } catch (e) { /* ignore */ }
    });
  }, [user, events]);

  const handleRegister = async (eventId) => {
    if (!user) { toast.error('Please log in to register for events'); return; }
    const token = getSessionToken();
    
    setRegLoading(prev => ({ ...prev, [eventId]: true }));
    try {
      const isRegistered = registrations[eventId]?.is_registered;
      const method = isRegistered ? 'DELETE' : 'POST';
      
      const res = await fetch(`${API}/events/${eventId}/register`, {
        method, credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const currentCount = registrations[eventId]?.attendee_count || 0;
        setRegistrations(prev => ({
          ...prev,
          [eventId]: {
            is_registered: !isRegistered,
            attendee_count: isRegistered ? currentCount - 1 : currentCount + 1
          }
        }));
        toast.success(isRegistered ? 'Unregistered from event' : 'Registered for event!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Registration failed');
      }
    } catch (e) {
      toast.error('Failed to update registration');
    } finally {
      setRegLoading(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesType = selectedType === 'all' || event.event_type === selectedType;
    return matchesType;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const getEventIcon = (eventType) => {
    const type = eventTypes.find(t => t.id === eventType);
    return type ? type.icon : CalendarDays;
  };

  return (
    <div className="bg-white" data-testid="events-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center">
            <span className="text-[#D42B3C] font-semibold text-sm tracking-wider uppercase mb-4 block">
              Events
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6">
              Connect, learn, and grow at AREA events
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Attend webinars, complete CE courses, and network with fellow professionals 
              at AREA events across the country.
            </p>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Calendar Sidebar */}
            <div className="lg:col-span-1">
              <Card className="border border-slate-200 shadow-lg sticky top-24">
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">Event Calendar</h3>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    className="rounded-md border"
                  />
                  <div className="mt-6 space-y-2">
                    <h4 className="font-semibold text-slate-700 text-sm">Upcoming Events</h4>
                    <div className="text-sm text-slate-600">
                      {events.slice(0, 3).map(event => (
                        <div key={event.event_id} className="py-2 border-b border-slate-100 last:border-0">
                          <div className="font-medium text-slate-900 truncate">{event.title}</div>
                          <div className="text-xs text-slate-500">{formatDate(event.date)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Events List */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="all" onValueChange={setSelectedType}>
                <TabsList className="flex flex-wrap gap-2 mb-8 bg-transparent">
                  <TabsTrigger 
                    value="all" 
                    className="data-[state=active]:bg-[#3D4F7A] data-[state=active]:text-white px-6 py-2 rounded-full"
                  >
                    All Events
                  </TabsTrigger>
                  {eventTypes.map(type => (
                    <TabsTrigger 
                      key={type.id}
                      value={type.id}
                      className="data-[state=active]:bg-[#3D4F7A] data-[state=active]:text-white px-6 py-2 rounded-full"
                    >
                      {type.name}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value={selectedType}>
                  {loading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A] mx-auto"></div>
                    </div>
                  ) : filteredEvents.length === 0 ? (
                    <div className="text-center py-12">
                      <CalendarDays className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No upcoming events in this category.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {filteredEvents.map((event) => {
                        const Icon = getEventIcon(event.event_type);
                        
                        return (
                          <Card 
                            key={event.event_id} 
                            className="border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300"
                          >
                            <CardContent className="p-6">
                              <div className="flex flex-col md:flex-row md:items-start gap-6">
                                {/* Date Badge */}
                                <div className="flex-shrink-0 text-center md:w-24">
                                  <div className="bg-[#3D4F7A] text-white rounded-xl p-4">
                                    <div className="text-sm font-medium uppercase">
                                      {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                                    </div>
                                    <div className="text-3xl font-bold">
                                      {new Date(event.date).getDate()}
                                    </div>
                                  </div>
                                </div>

                                {/* Event Details */}
                                <div className="flex-1">
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <Badge variant="secondary" className="bg-[#3D4F7A]/10 text-[#3D4F7A]">
                                      <Icon className="w-3 h-3 mr-1" />
                                      {eventTypes.find(t => t.id === event.event_type)?.name || event.event_type}
                                    </Badge>
                                    {event.is_virtual ? (
                                      <Badge variant="outline" className="border-green-500 text-green-600">
                                        <Video className="w-3 h-3 mr-1" />
                                        Virtual
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="border-blue-500 text-blue-600">
                                        <MapPin className="w-3 h-3 mr-1" />
                                        In-Person
                                      </Badge>
                                    )}
                                  </div>
                                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                                    {event.title}
                                  </h3>
                                  <p className="text-slate-600 text-sm mb-4">{event.description}</p>
                                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-4">
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-4 h-4" />
                                      {formatTime(event.date)}
                                    </span>
                                    {event.location && (
                                      <span className="flex items-center gap-1">
                                        <MapPin className="w-4 h-4" />
                                        {event.location}
                                      </span>
                                    )}
                                    {event.max_attendees && (
                                      <span className="flex items-center gap-1">
                                        <Users className="w-4 h-4" />
                                        {registrations[event.event_id]?.attendee_count || 0} / {event.max_attendees}
                                      </span>
                                    )}
                                  </div>
                                  {user ? (
                                    registrations[event.event_id]?.is_registered ? (
                                      <Button 
                                        variant="outline"
                                        className="border-[#3D4F7A] text-[#3D4F7A]" 
                                        onClick={() => handleRegister(event.event_id)}
                                        disabled={regLoading[event.event_id]}
                                        data-testid={`event-unregister-${event.event_id}`}
                                      >
                                        {regLoading[event.event_id] ? (
                                          <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                        ) : (
                                          <CheckCircle className="mr-2 w-4 h-4" />
                                        )}
                                        Registered
                                      </Button>
                                    ) : (
                                      <Button 
                                        className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white" 
                                        onClick={() => handleRegister(event.event_id)}
                                        disabled={regLoading[event.event_id]}
                                        data-testid={`event-register-${event.event_id}`}
                                      >
                                        {regLoading[event.event_id] ? (
                                          <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                        ) : (
                                          <ArrowRight className="mr-2 w-4 h-4" />
                                        )}
                                        Register Now
                                      </Button>
                                    )
                                  ) : (
                                    <Link to="/register">
                                      <Button className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white">
                                        Join to Register
                                        <ArrowRight className="ml-2 w-4 h-4" />
                                      </Button>
                                    </Link>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#3D4F7A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <GraduationCap className="w-16 h-16 text-white mx-auto mb-6" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Never stop learning
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            AREA members get priority access to all events, networking mixers, and exclusive discounts on continuing education.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-white text-[#3D4F7A] hover:bg-slate-100 px-10 py-6 text-lg font-semibold"
            >
              Join AREA
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
